import { useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Phone, Send, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ZONES = [
  {
    id: "boulogne-billancourt",
    name: "Boulogne-Billancourt",
    desc: "Notre auto-école et zone d'enseignement principale.",
    accent: "#00a86b",
    badge: "Siège",
  },
  {
    id: "porte-saint-cloud",
    name: "Porte de Saint-Cloud",
    desc: "Carrefour majeur du 16ᵉ arrondissement.",
    accent: "#1368ce",
    badge: "Paris 16ᵉ",
  },
  {
    id: "porte-auteuil",
    name: "Porte d'Auteuil",
    desc: "Avenues larges et grands carrefours du 16ᵉ.",
    accent: "#8b5cf6",
    badge: "Paris 16ᵉ",
  },
  {
    id: "place-italie",
    name: "Place d'Italie",
    desc: "Place giratoire et circulation dense du 13ᵉ.",
    accent: "#FF8C42",
    badge: "Paris 13ᵉ",
  },
];

const FORMATIONS = ["Permis B (boîte manuelle)", "Permis B (boîte automatique)", "Conduite accompagnée (AAC)", "Permis B accéléré", "Passerelle BVA → BVM", "Heures supplémentaires", "Autre"];

export default function Reservation() {
  const [zoneId, setZoneId] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", formation: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const zone = useMemo(() => ZONES.find(z => z.id === zoneId), [zoneId]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading || !zone) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form, zone: zone.name,
        message: `[Réservation depuis le point de départ : ${zone.name}]\n\n${form.message || ""}`,
      });
      setDone(true);
      toast.success("Demande envoyée ! Nous vous recontactons sous 24h.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur lors de l'envoi.";
      toast.error(typeof msg === "string" ? msg : "Erreur lors de l'envoi.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#f8faff] text-[#1a2332] font-body">
        <div className="max-w-2xl mx-auto px-6 md:px-8 py-24 text-center" data-testid="reservation-success">
          <div className="w-20 h-20 rounded-full bg-[#00a86b]/10 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-12 h-12 text-[#00a86b]" />
          </div>
          <h1 className="mt-6 font-headings font-black text-3xl md:text-4xl">Demande envoyée !</h1>
          <p className="mt-4 text-[#5b6577]">
            Nous vous recontacterons sous 24h pour planifier votre première leçon au départ de
            <strong className="text-[#1a2332]"> {zone?.name}</strong>.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/" className="bg-[#1a2332] hover:bg-[#FF8C42] text-white font-headings font-bold rounded-xl px-5 py-2.5 text-sm transition-colors">
              Retour à l&apos;accueil
            </Link>
            <a href="tel:+33141315061" className="inline-flex items-center gap-2 bg-white border border-black/10 text-[#1a2332] hover:border-[#00a86b] rounded-xl px-5 py-2.5 text-sm font-headings font-bold">
              <Phone className="w-4 h-4" /> 01 41 31 50 61
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8faff] text-[#1a2332] font-body">
      <Seo
        title="Réserver une leçon de conduite · Boulogne, Paris 16ᵉ & 13ᵉ | DDSBillancourt"
        description="Réservez votre leçon de conduite au départ de Boulogne-Billancourt, Porte de Saint-Cloud, Porte d'Auteuil ou Place d'Italie. Réponse rapide, conduite 7j/7 de 7h à 22h."
        path="/reservation"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://ddsbillancourt.fr/" },
            { "@type": "ListItem", position: 2, name: "Réservation", item: "https://ddsbillancourt.fr/reservation" },
          ],
        }}
      />
      <div className="max-w-5xl mx-auto px-6 md:px-8 py-12 md:py-20">
        <Link to="/" className="inline-flex items-center gap-2 text-[#5b6577] hover:text-[#00a86b] text-sm font-headings font-semibold mb-8" data-testid="reservation-back">
          <ArrowLeft className="w-4 h-4" /> Retour à l&apos;accueil
        </Link>

        <div className="text-[#FF8C42] font-headings font-bold text-sm uppercase tracking-[0.2em]">Réservation</div>
        <h1 className="mt-3 font-headings font-black text-4xl md:text-5xl tracking-tight">
          Choisissez votre point de départ
        </h1>
        <p className="mt-4 text-[#5b6577] max-w-2xl">
          Sélectionnez la zone où vous souhaitez démarrer vos leçons. Un de nos moniteurs viendra vous y prendre en charge.
        </p>

        {/* Step 1: zone picker */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="zone-picker">
          {ZONES.map((z) => {
            const selected = zoneId === z.id;
            return (
              <button key={z.id} type="button" onClick={() => setZoneId(z.id)}
                data-testid={`pick-zone-${z.id}`}
                className={`text-left bg-white rounded-2xl p-5 border-2 transition-all ${selected ? "border-[#FF8C42] shadow-lg scale-[1.02]" : "border-black/5 hover:border-[#1368ce]/30"}`}>
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: z.accent }}>
                    <MapPin className="w-5 h-5" />
                  </div>
                  {selected && <span className="text-[10px] font-headings font-bold bg-[#FF8C42] text-white px-2 py-1 rounded">CHOISI</span>}
                </div>
                <div className="mt-3 font-headings font-bold text-[#1a2332]">{z.name}</div>
                <div className="text-[10px] uppercase tracking-wide font-headings font-bold mt-0.5" style={{ color: z.accent }}>{z.badge}</div>
                <div className="mt-2 text-xs text-[#5b6577] leading-relaxed">{z.desc}</div>
              </button>
            );
          })}
        </div>

        {/* Step 2: form (revealed when a zone is selected) */}
        {zone && (
          <form onSubmit={submit} data-testid="reservation-form"
            className="mt-10 bg-white rounded-2xl border border-black/5 shadow-lg p-6 md:p-10 max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#FF8C42]/10 text-[#FF8C42] rounded-full px-3 py-1 text-xs font-headings font-bold mb-6">
              <MapPin className="w-3 h-3" /> Point de départ : {zone.name}
            </div>
            <h2 className="font-headings font-black text-2xl text-[#1a2332]">Vos coordonnées</h2>
            <p className="mt-1 text-sm text-[#5b6577]">Nous vous recontactons sous 24h.</p>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nom complet *">
                <input required value={form.name} onChange={update("name")} className="input-dds" data-testid="res-name" placeholder="Marie Dupont" />
              </Field>
              <Field label="Téléphone *">
                <input required type="tel" value={form.phone} onChange={update("phone")} className="input-dds" data-testid="res-phone" placeholder="06 12 34 56 78" />
              </Field>
              <Field label="Email *">
                <input required type="email" value={form.email} onChange={update("email")} className="input-dds" data-testid="res-email" placeholder="vous@exemple.fr" />
              </Field>
              <Field label="Formation souhaitée *">
                <select required value={form.formation} onChange={update("formation")} className="input-dds" data-testid="res-formation">
                  <option value="">Choisir…</option>
                  {FORMATIONS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </Field>
              <Field label="Message (optionnel)" className="md:col-span-2">
                <textarea rows={4} value={form.message} onChange={update("message")} className="input-dds resize-none" data-testid="res-message"
                  placeholder="Précisez vos disponibilités, niveau actuel, questions…" />
              </Field>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button type="submit" disabled={loading}
                className="inline-flex items-center gap-2 bg-[#FF8C42] hover:bg-[#e8763a] disabled:opacity-50 text-white font-headings font-bold rounded-xl px-6 py-3 text-sm shadow-lg"
                data-testid="res-submit">
                <Send className="w-4 h-4" />
                {loading ? "Envoi…" : "Envoyer ma demande"}
              </button>
              <a href="tel:+33141315061" className="inline-flex items-center gap-2 text-[#1a2332] hover:text-[#00a86b] text-sm font-headings font-bold">
                <Phone className="w-4 h-4" /> Ou appelez-nous : 01 41 31 50 61
              </a>
            </div>
            <p className="mt-4 text-xs text-[#5b6577]">En soumettant ce formulaire vous acceptez d&apos;être recontacté par DDSBillancourt.</p>
          </form>
        )}

        <style>{`.input-dds{width:100%;background:#f8faff;border:1px solid #e5eaf3;border-radius:.75rem;padding:.7rem .9rem;color:#1a2332;font-size:.9rem;outline:none;transition:border-color .15s}.input-dds:focus{border-color:#FF8C42}`}</style>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] font-headings font-bold uppercase tracking-wide text-[#5b6577] mb-1.5">{label}</span>
      {children}
    </label>
  );
}
